import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import SlotMachine from '../components/games/SlotMachine';
import SpinWheel from '../components/games/SpinWheel';
import ScratchCard from '../components/games/ScratchCard';
import MemoryGame from '../components/games/MemoryGame';
import { STokenLogo } from '../components/StreamDisplay';
import { useAdManager } from '../hooks/useAdManager';
import './Games.css';

const DAILY_CARD = {
  id: 'daily',
  title: 'Daily Bonus',
  desc: () => <>Free • Streak rewards • Up to 31 days</>,
  maxWin: null,
  icon: '📅',
  accentColor: 'var(--rarity-uncommon)',
  route: '/daily-bonus',
  cost: 0,
};

const GAME_CARDS = [
  {
    id: 'slots',
    title: 'Slot Machine',
    desc: (s) => <>325{s()} / spin</>,
    maxWin: 10000,
    icon: '🎰',
    accentColor: 'var(--brand)',
    component: SlotMachine,
    cost: 325,
  },
  {
    id: 'spin',
    title: 'Spin Wheel',
    desc: (s) => <>450{s()} / spin • 10 slices</>,
    maxWin: 1000,
    icon: '🎡',
    accentColor: 'var(--accent)',
    component: SpinWheel,
    cost: 450,
  },
  {
    id: 'scratch',
    title: 'Scratch Card',
    desc: (s) => <>50{s()} / card • Match 3 to win</>,
    maxWin: 5000,
    icon: '🎟️',
    accentColor: 'var(--rarity-epic)',
    component: ScratchCard,
    cost: 50,
  },
  {
    id: 'memory',
    title: 'Memory Game',
    desc: () => <>Free • 4 rounds • 60s → 15s</>,
    maxWin: 375,
    icon: '🧠',
    accentColor: 'var(--brand)',
    component: MemoryGame,
    cost: 0,
  },
];

export default function Games() {
  const [activeGame, setActiveGame]   = useState(null);
  const [userData, setUserData]       = useState(null);
  const [insufficientGame, setInsuff] = useState(null); // game id with insufficient funds
  const navigate = useNavigate();
  const { showSilentAd } = useAdManager();
  const unsubRef = useRef(null);

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      unsubRef.current?.();
      if (!user) return;
      unsubRef.current = onSnapshot(doc(db, 'users', user.uid), (snap) => {
        if (snap.exists()) setUserData(snap.data());
      });
    });
    return () => { unsubAuth(); unsubRef.current?.(); };
  }, []);

  const active = GAME_CARDS.find(g => g.id === activeGame);

  const handleCloseGame = () => {
    setActiveGame(null);
    showSilentAd();
  };

  const handleOpenGame = (game) => {
    if (game.locked) return;
    if (game.route) { navigate(game.route); return; }

    // Dev accounts bypass balance check
    const isDev = userData?.isDev;

    if (!isDev && game.cost > 0) {
      const balance = userData?.totalStreams || 0;
      if (balance < game.cost) {
        setInsuff(game.id);
        setTimeout(() => setInsuff(null), 2500);
        return;
      }
    }

    setActiveGame(game.id);
  };

  const balance = userData?.totalStreams || 0;

  return (
    <div className="page games">
      <div className="grid-bg" aria-hidden="true" />

      <header className="games__header">
        <button className="page-back-btn" onClick={() => navigate('/')}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
          Home
        </button>
        <h1 className="games__title">Games</h1>
        <p className="games__subtitle">Win Streams. Climb the ranks.</p>
      </header>

      {/* Insufficient funds toast */}
      {insufficientGame && (
        <div className="games__insufficient">
          <STokenLogo size={14} />
          <span>Not enough Streams to play</span>
        </div>
      )}

      <div className="games__grid">
        {[DAILY_CARD, ...GAME_CARDS].map(game => {
          const canAfford = userData?.isDev || game.cost === 0 || balance >= game.cost;
          return (
            <button
              key={game.id}
              className={`card games__card${game.locked ? ' games__card--locked' : ''}${!canAfford ? ' games__card--broke' : ''}`}
              style={{ '--game-color': game.accentColor }}
              onClick={() => handleOpenGame(game)}
              disabled={game.locked}
              aria-label={game.title}
            >
              <div className="games__card-icon">{game.icon}</div>
              <div className="games__card-info">
                <div className="games__card-title">{game.title}</div>
                <div className="games__card-desc" style={{ display:'flex', alignItems:'center', gap:2 }}>
                  {game.desc(() => <STokenLogo size={11} />)}
                </div>
                {game.maxWin && (
                  <div className="games__card-maxwin">
                    <span>Max win</span>
                    <span style={{ display:'inline-flex', alignItems:'center', gap:2 }}>
                      {game.maxWin.toLocaleString()}<STokenLogo size={10} />
                    </span>
                  </div>
                )}
              </div>
              {!game.locked && <div className="games__card-arrow">›</div>}
              {game.locked && <div className="games__card-lock">🔒</div>}
              <div className="games__card-glow" />
            </button>
          );
        })}
      </div>

      {/* Game modal */}
      {activeGame && active?.component && (
        <div className="games__modal-overlay" onClick={handleCloseGame}>
          <div className="games__modal" onClick={e => e.stopPropagation()}>
            <button className="games__modal-close" onClick={handleCloseGame} aria-label="Close">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
            <active.component onClose={handleCloseGame} />
          </div>
        </div>
      )}
    </div>
  );
}
